import { useCallback, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import { createUsersApi } from '../api/usersApi';
import type { User } from '../userTypes';

interface UseUpdateUserStatusResult {
  updateUserStatus: (id: string, isActive: boolean) => Promise<User | null>;
  loading: boolean;
  error: ApiError | Error | null;
}

/** Mutation hook pour PATCH /users/:id/status (activation/désactivation). */
export function useUpdateUserStatus(): UseUpdateUserStatusResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => createUsersApi(client), [client]);
  const { notifyError } = useNotification();

  const [error, setError] = useState<ApiError | Error | null>(null);

  const updateUserStatus = useCallback(
    async (id: string, isActive: boolean): Promise<User | null> => {
      try {
        setError(null);
        return await api.updateStatus(id, { is_active: isActive });
      } catch (err) {
        const e = err as ApiError | Error;
        setError(e);
        if (ApiError.isApiError(e) && e.isServerError) notifyError(e);
        else if (!ApiError.isApiError(e)) notifyError(e);
        return null;
      }
    },
    [api, notifyError],
  );

  return { updateUserStatus, loading, error };
}
